import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ledgerApi, unwrap, unwrapList } from "../api/resources";
import { useAuth } from "../context/AuthContext";

const DayLockContext = createContext(null);

export function DayLockProvider({ children }) {
  const { isAdmin, builderScope } = useAuth();
  const [statusByDate, setStatusByDate] = useState({});
  const [todayDate, setTodayDate] = useState("");
  const enabled = !isAdmin || Boolean(builderScope);

  const load = useCallback(async () => {
    if (!enabled) {
      setStatusByDate({});
      setTodayDate("");
      return;
    }

    const query = isAdmin ? { builderId: builderScope } : {};

    try {
      const [listRes, todayRes] = await Promise.all([
        ledgerApi.list({ ...query, limit: 60 }),
        ledgerApi.today(query),
      ]);
      const next = {};
      unwrapList(listRes).items.forEach((item) => {
        if (item.date) next[item.date] = item.status;
      });
      const today = unwrap(todayRes);
      if (today?.date) {
        next[today.date] = today.status;
        setTodayDate(today.date);
      }
      setStatusByDate(next);
    } catch {
      setStatusByDate({});
      setTodayDate("");
    }
  }, [enabled, isAdmin, builderScope]);

  useEffect(() => {
    load();
  }, [load]);

  const isClosed = useCallback((date) => Boolean(date) && statusByDate[date] === "closed", [statusByDate]);
  const anyClosed = useCallback((dates = []) => dates.some((date) => isClosed(date)), [isClosed]);
  const isTodayClosed = Boolean(todayDate) && statusByDate[todayDate] === "closed";

  const value = useMemo(
    () => ({
      isClosed,
      anyClosed,
      isTodayClosed,
      todayDate,
      statusByDate,
      reload: load,
    }),
    [isClosed, anyClosed, isTodayClosed, todayDate, statusByDate, load]
  );

  return <DayLockContext.Provider value={value}>{children}</DayLockContext.Provider>;
}

export function useDayLock() {
  const ctx = useContext(DayLockContext);
  if (!ctx) throw new Error("useDayLock must be used within DayLockProvider");
  return ctx;
}
